import {fetchCAPI} from '../utils/fetch-capi'
import {taskByIdApiPath} from '../utils/api-url-helpers'
import {useCAPIQuery} from './useCAPIQuery'
import type {HydratedAPITask, RawHydratedAPITask} from '../types/api-task'
import {normalizeHydratedAPITask} from '../types/api-task'
import {useCallback, useEffect} from 'react'
import {useQueryClient} from '@github-ui/react-query'
import {useDebounce} from '@github-ui/use-debounce'
import {useAgentSessionsAlive, type AgentSessionAlivePayload} from './use-agent-sessions-alive'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {getInteractiveRefetchInterval, isNonTerminalInteractiveAPITask} from '../agents/interactive-polling'
import {syncTaskListsFromAPITask} from '../utils/task-helpers'

export const agentTaskQueryKey = (owner?: string, repo?: string, taskId?: string) => ['agent-task', taskId, owner, repo]

export const fetchTask = async ({taskId}: {taskId: string}) => {
  const path = taskByIdApiPath(taskId)
  const response = await fetchCAPI({path})
  const data = await response.json()
  // Handle both wrapped { task: {...} } (legacy) and unwrapped root-level response
  const raw = (data.task ?? data) as RawHydratedAPITask
  return normalizeHydratedAPITask(raw)
}

export function useTask({
  owner,
  repo,
  taskId,
  initialTask,
  sessionsPollingInterval = 3000,
}: {
  owner?: string
  repo?: string
  taskId?: string
  initialTask?: HydratedAPITask
  sessionsPollingInterval?: number
}) {
  const queryClient = useQueryClient()
  const handleAliveUpdate = useCallback(
    (payload?: AgentSessionAlivePayload) => {
      // Only invalidate the task query if the update is related to the current task
      if (payload?.task_id === taskId) {
        queryClient.invalidateQueries({
          queryKey: agentTaskQueryKey(owner, repo, taskId),
        })
      }
    },
    [queryClient, owner, repo, taskId],
  )

  const debouncedHandleAliveUpdate = useDebounce(handleAliveUpdate, sessionsPollingInterval, {maxWait: 10000})

  const isTaskAliveEnabled = isFeatureEnabled('copilot_mission_control_task_alive_updates') && !!taskId

  useAgentSessionsAlive({
    onEvent: debouncedHandleAliveUpdate,
    enableCreateEvents: false,
    enableUpdateEvents: true,
    enableLogUpdates: false,
    enableTaskUpdates: true,
    enabled: isTaskAliveEnabled,
  })

  const query = useCAPIQuery<HydratedAPITask>({
    queryKey: agentTaskQueryKey(owner, repo, taskId),
    queryFn: () => {
      if (!taskId) {
        throw new Error('taskId is required')
      }

      return fetchTask({
        taskId,
      })
    },
    initialData: initialTask,
    enabled: !!taskId,
    // CLI tasks need to be refetched more frequently to reflect real-time updates. We also only fetch them
    // when we know that the task is not "terminal", meaning that there could be more data coming in.
    refetchInterval: ({state: {data: task}}) => getInteractiveRefetchInterval(isNonTerminalInteractiveAPITask(task)),
    // The Remote CLI behavior is best suited being as close to real-time as possible,
    // so fetching fresh data on refocus helps.
    refetchOnWindowFocus: isFeatureEnabled('copilot_mission_control_cli_session_status'),
  })

  // Propagate the latest task data into any cached task lists so they reflect
  // status updates from this query (e.g., CLI session state) without waiting
  // for their own refetch cycle.
  useEffect(() => {
    if (query.data) {
      syncTaskListsFromAPITask(queryClient, query.data)
    }
  }, [query.data, queryClient])

  return {
    data: query.data,
    dataUpdatedAt: query.dataUpdatedAt,
    isLoading: query.isLoading && !query.data,
    isError: query.isError,
  }
}
